import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { initFirebase } from '../firebase';

/**
 * Business service for fetching business data from Firestore
 * Single business per customer model - business data stored at customer root level
 */
class BusinessService {
  constructor() {
    const { db } = initFirebase();
    this.db = db;
  }

  /**
   * Get all active businesses
   * @returns {Promise<Array>} Array of business objects
   */
  async getAllBusinesses() {
    try {
      const customersRef = collection(this.db, 'customers');
      const customersQuery = query(customersRef, where('active', '==', true));
      const customersSnapshot = await getDocs(customersQuery);

      const businesses = customersSnapshot.docs.map((customerDoc) => ({
        id: customerDoc.id,
        customerId: customerDoc.id,
        ...customerDoc.data(),
      }));

      return businesses;
    } catch (error) {
      console.error('Error fetching businesses:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  /**
   * Get featured businesses
   * @param {number} limit - Maximum number of featured businesses
   * @returns {Promise<Array>} Array of featured business objects
   */
  async getFeaturedBusinesses(limit = 6) {
    try {
      const allBusinesses = await this.getAllBusinesses();
      return allBusinesses
        .filter((b) => b.featured)
        .sort((a, b) => (b.rating || 0) - (a.rating || 0))
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching featured businesses:', error);
      throw error;
    }
  }

  /**
   * Get a business by customer ID
   * @param {string} customerId - Customer ID (same as business ID in single business model)
   * @returns {Promise<Object|null>} Business object or null
   */
  async getBusinessById(customerId) {
    try {
      const customerRef = doc(this.db, 'customers', customerId);
      const customerDoc = await getDoc(customerRef);
      
      if (!customerDoc.exists()) {
        return null;
      }

      return {
        id: customerDoc.id,
        customerId: customerDoc.id,
        ...customerDoc.data(),
      };
    } catch (error) {
      console.error('Error fetching business:', error);
      throw error;
    }
  }

  /**
   * Get calendars for a customer
   * @param {string} customerId - Customer ID
   * @returns {Promise<Array>} Array of calendar objects
   */
  async getCalendars(customerId) {
    try {
      const calendarsRef = collection(this.db, 'customers', customerId, 'calendars');
      const calendarsSnapshot = await getDocs(calendarsRef);
      
      return calendarsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching calendars:', error);
      throw error;
    }
  }

  /**
   * Get calendar app settings for a customer
   * @param {string} customerId - Customer ID
   * @returns {Promise<Object|null>} Calendar app settings or null
   */
  async getCalendarAppSettings(customerId) {
    try {
      const settingsRef = doc(this.db, 'customers', customerId, 'calendar_app', 'settings');
      const settingsDoc = await getDoc(settingsRef);
      
      if (!settingsDoc.exists()) {
        return null;
      }

      return settingsDoc.data();
    } catch (error) {
      console.error('Error fetching calendar app settings:', error);
      throw error;
    }
  }

  /**
   * Search businesses by query
   * @param {string} searchQuery - Search query string
   * @returns {Promise<Array>} Array of matching businesses
   */
  async searchBusinesses(searchQuery) {
    try {
      const allBusinesses = await this.getAllBusinesses();
      const query = searchQuery.toLowerCase();
      
      return allBusinesses.filter((business) => {
        const name = (business.business_name || '').toLowerCase();
        const description = (business.description || '').toLowerCase();
        const category = (business.category || '').toLowerCase();
        
        return name.includes(query) || 
               description.includes(query) || 
               category.includes(query);
      });
    } catch (error) {
      console.error('Error searching businesses:', error);
      throw error;
    }
  }
}

export const businessService = new BusinessService();
export default BusinessService;
